import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Select, Input } from 'antd';
import { debounce } from 'lodash';
import { DateTime } from 'luxon';
import { AdministrationHistoryStore } from '../../stores';
import DatePicker from '../../../../components/DatePicker';
import './AdministrationHistoryFilters.less';

interface Props {
    store: AdministrationHistoryStore;
}

const AdministrationHistoryFilters: React.FC<Props> = ({ store }) => {
    const mapStoredDateToPickerDate = (value?: string) => {
        if (!value) {
            return null;
        }

        const dateTime = DateTime.fromISO(value, { setZone: true });

        if (!dateTime.isValid) {
            return null;
        }

        const utcCalendarDate = dateTime.toUTC();

        return new Date(utcCalendarDate.year, utcCalendarDate.month - 1, utcCalendarDate.day);
    };

    const mapPickerDateToUtcIso = (value: Date, endOfDay: boolean) => {
        const localDate = DateTime.fromJSDate(value);
        const utcDate = DateTime.utc(localDate.year, localDate.month, localDate.day);

        return (endOfDay ? utcDate.endOf('day') : utcDate.startOf('day')).toISO() ?? undefined;
    };

    const debouncedSearch = React.useMemo(
        () =>
            debounce(() => {
                store.performSearch();
            }, 500),
        [store]
    );

    return (
        <Form className="administration-history-filter-form" layout="inline">
            <Form.Item>
                <Select
                    allowClear
                    style={{ width: 300 }}
                    placeholder="Select area..."
                    value={store.selectedAreaOption}
                    onChange={(value?: string) => {
                        store.setSelectedAreaOption(value);
                        store.performSearch();
                    }}
                    options={AdministrationHistoryStore.historyAreaOptions}
                />
            </Form.Item>

            <Form.Item>
                <Input.Search
                    style={{ width: 300 }}
                    value={store.searchTerm}
                    placeholder="Search by entity name..."
                    onChange={e => {
                        store.setSearchTerm(e.target.value);
                        debouncedSearch();
                    }}
                    onSearch={value => {
                        store.setSearchTerm(value);
                        store.performSearch();
                    }}
                    allowClear
                />
            </Form.Item>

            <Form.Item>
                <Select
                    className="user-select"
                    placeholder="Select user"
                    style={{ width: 300 }}
                    value={store.userId}
                    onChange={(value?: string) => {
                        store.setUserId(value);
                        store.performSearch();
                    }}
                    options={store.userFilterOptions.map(user => ({
                        value: user.id,
                        label: user.userName,
                        title: user.userName
                    }))}
                    filterOption={(input, option) =>
                        option?.title && option.title.toLowerCase().includes(input.toLowerCase())
                    }
                    loading={store.isLoadingUsers}
                    showSearch
                    allowClear
                />
            </Form.Item>

            <Form.Item>
                <DatePicker.RangePicker
                    style={{ width: 300 }}
                    className="date-picker"
                    value={[mapStoredDateToPickerDate(store.dateFrom), mapStoredDateToPickerDate(store.dateTo)]}
                    onChange={(dateRange: [Date | null, Date | null] | null) => {
                        let dateFrom: string | undefined;
                        let dateTo: string | undefined;

                        if (dateRange && dateRange[0] && dateRange[1]) {
                            dateFrom = mapPickerDateToUtcIso(dateRange[0], false);
                            dateTo = mapPickerDateToUtcIso(dateRange[1], true);
                        }

                        store.setDateFrom(dateFrom);
                        store.setDateTo(dateTo);
                        store.performSearch();
                    }}
                />
            </Form.Item>
        </Form>
    );
};

export default observer(AdministrationHistoryFilters);
